import React, { useState } from 'react'
import { client } from '../../client'
import useEffectAsync from '../../hooks/useEffectAsync'
import { PageLoadingIndicator } from '../../components/PageLoadingIndicator'
import { useTranslation } from 'react-i18next'
import { format } from 'date-fns'
import { nl as nlLocale } from 'date-fns/locale'
import AuthenticationService from '../../services/AuthenticationService'
import PeopleAltOutlinedIcon from '@material-ui/icons/PeopleAltOutlined'
import AssignmentTurnedInTwoToneIcon from '@material-ui/icons/AssignmentTurnedInTwoTone'
import AssignmentTurnedInIcon from '@material-ui/icons/AssignmentTurnedIn'
import PersonIcon from '@material-ui/icons/Person'
import EditIcon from '@material-ui/icons/Edit'
import Typography from '@material-ui/core/Typography'
import { CollapsiblePatientSection } from './components/CollapsiblePatientSection'

interface Patient {
  id: number
  firstName: string
  infix?: string
  lastName: string
  birthDate?: string
  lastModifiedAt: string
  practice?: { name: string }
  language?: string
  onsite?: boolean
  online?: boolean
  practiceName?: string
  intakeWarning?: boolean
}

interface ProcessedPatient extends Patient {
  fullName: string
  shortBirthDate?: string
}

interface PatientCount {
  numPatients: number
  caseload?: number
}

interface PatientListData {
  patients: ProcessedPatient[]
  count: PatientCount
  loading: boolean
}

const PatientSelectPageContentNew = () => {
  const { t } = useTranslation('patientSelectPage')
  const [loading, setLoading] = useState(true)
  
  // Track which sections have loaded their data
  const [loadedSections, setLoadedSections] = useState<Record<string, boolean>>({
    caseload: false,
    completedTreatments: false,
    coPractitioner: false,
    completedCoPractitioner: false
  })
  
  const [caseloadData, setCaseloadData] = useState<PatientListData>({
    patients: [],
    count: { numPatients: 0, caseload: 0 },
    loading: false
  })
  
  const [completedTreatmentsData, setCompletedTreatmentsData] = useState<PatientListData>({
    patients: [],
    count: { numPatients: 0 },
    loading: false
  })
  
  const [coPractitionerData, setCoPractitionerData] = useState<PatientListData>({
    patients: [],
    count: { numPatients: 0 },
    loading: false
  })
  
  const [completedCoPractitionerData, setCompletedCoPractitionerData] = useState<PatientListData>({
    patients: [],
    count: { numPatients: 0 },
    loading: false
  })

  const isPractitioner = AuthenticationService.isPractitioner()

  const [originalData, setOriginalData] = useState<{
    patients: ProcessedPatient[]
    count: PatientCount
    loading: boolean
  }>({
    patients: [],
    count: { numPatients: 0 },
    loading: true
  })

  const getFullName = (patient: Patient) => {
    const { firstName, infix, lastName } = patient
    return [firstName, infix, lastName].filter(Boolean).join(' ')
  }

  const isValidDate = (date: any) => {
    if (!date) return false
    const d = new Date(date)
    return d instanceof Date && !isNaN(d.getTime())
  }

  const processPatientData = (patients: Patient[]): ProcessedPatient[] => {
    if (!patients || !patients.length) return []

    return patients.map((patient) => {
      const processedPatient: ProcessedPatient = {
        ...patient,
        fullName: getFullName(patient)
      }

      const { birthDate, lastModifiedAt } = patient
      if (birthDate && isValidDate(birthDate)) {
        processedPatient.shortBirthDate = format(new Date(birthDate), 'P', {
          locale: nlLocale
        })
      }

      if (lastModifiedAt && isValidDate(lastModifiedAt)) {
        processedPatient.lastModifiedAt = format(new Date(lastModifiedAt), 'Pp', {
          locale: nlLocale
        })
      }

      return processedPatient
    })
  }

  // Intake warnings are now calculated on the backend - no need for frontend processing!

  const loadCaseloadData = async (pageSize = 500, page = 0) => {
    if (loadedSections.caseload && page === 0) return // Already loaded initial data
    
    try {
      setCaseloadData(prev => ({ ...prev, loading: page === 0 })) // Only show loading for initial load
      
      const patients = await client.getCaseloadPatients(pageSize, page)
      const processedPatients = processPatientData(patients)
      
      // Intake warnings are now included in the backend response - no additional processing needed! 🚀

      setCaseloadData(prev => ({
        ...prev,
        patients: page === 0 ? processedPatients : [...prev.patients, ...processedPatients],
        loading: false
      }))
      
      if (page === 0) {
        setLoadedSections(prev => ({ ...prev, caseload: true }))
      }
    } catch (error) {
      console.error('Error loading caseload data:', error)
      setCaseloadData(prev => ({ ...prev, loading: false }))
    }
  }

  const loadCompletedTreatmentsData = async (pageSize = 100, page = 0) => {
    if (loadedSections.completedTreatments && page === 0) return // Already loaded initial data
    
    try {
      setCompletedTreatmentsData(prev => ({ ...prev, loading: page === 0 }))
      
      const patients = await client.getCompletedTreatments(pageSize, page)
      const processedPatients = processPatientData(patients)

      setCompletedTreatmentsData(prev => ({
        ...prev,
        patients: page === 0 ? processedPatients : [...prev.patients, ...processedPatients],
        loading: false
      }))
      
      if (page === 0) {
        setLoadedSections(prev => ({ ...prev, completedTreatments: true }))
      }
    } catch (error) {
      console.error('Error loading completed treatments data:', error)
      setCompletedTreatmentsData(prev => ({ ...prev, loading: false }))
    }
  }

  const loadCoPractitionerData = async (pageSize = 100, page = 0) => {
    if (loadedSections.coPractitioner && page === 0) return // Already loaded initial data
    
    try {
      setCoPractitionerData(prev => ({ ...prev, loading: page === 0 }))
      
      const patients = await client.getCoPractitionerPatients(pageSize, page)
      const processedPatients = processPatientData(patients)

      setCoPractitionerData(prev => ({
        ...prev,
        patients: page === 0 ? processedPatients : [...prev.patients, ...processedPatients],
        loading: false
      }))
      
      if (page === 0) {
        setLoadedSections(prev => ({ ...prev, coPractitioner: true }))
      }
    } catch (error) {
      console.error('Error loading co-practitioner data:', error)
      setCoPractitionerData(prev => ({ ...prev, loading: false }))
    }
  }

  const loadCompletedCoPractitionerData = async (pageSize = 100, page = 0) => {
    if (loadedSections.completedCoPractitioner && page === 0) return // Already loaded initial data
    
    try {
      setCompletedCoPractitionerData(prev => ({ ...prev, loading: page === 0 }))
      
      const patients = await client.getCompletedCoPractitionerTreatments(pageSize, page)
      const processedPatients = processPatientData(patients)

      setCompletedCoPractitionerData(prev => ({
        ...prev,
        patients: page === 0 ? processedPatients : [...prev.patients, ...processedPatients],
        loading: false
      }))
      
      if (page === 0) {
        setLoadedSections(prev => ({ ...prev, completedCoPractitioner: true }))
      }
    } catch (error) {
      console.error('Error loading completed co-practitioner data:', error)
      setCompletedCoPractitionerData(prev => ({ ...prev, loading: false }))
    }
  }

  const loadOriginalData = async () => {
    try {
      setOriginalData(prev => ({ ...prev, loading: true }))
      
      const [patients, count] = await Promise.all([
        client.getPatients(20, 0),
        client.getPatientCount()
      ])

      const processedPatients = processPatientData(patients)

      setOriginalData({
        patients: processedPatients,
        count,
        loading: false
      })
    } catch (error) {
      console.error('Error loading original data:', error)
      setOriginalData(prev => ({ ...prev, loading: false }))
    }
  }

  const loadAllCounts = async () => {
    try {
      const counts = await client.getPatientCounts()
      
      setCaseloadData(prev => ({ ...prev, count: { numPatients: counts.caseload } }))
      setCompletedTreatmentsData(prev => ({ ...prev, count: { numPatients: counts.completedTreatments } }))
      setCoPractitionerData(prev => ({ ...prev, count: { numPatients: counts.coPractitioner } }))
      setCompletedCoPractitionerData(prev => ({ ...prev, count: { numPatients: counts.completedCoPractitioner } }))
    } catch (error) {
      console.error('Error loading patient counts:', error)
    }
  }

  useEffectAsync(async () => {
    setLoading(true)
    
    if (isPractitioner) {
      // Load only essential data initially - caseload (expanded by default) and counts
      await Promise.all([
        loadAllCounts(),
        loadCaseloadData()
      ])
    } else {
      await loadOriginalData()
    }
    
    setLoading(false)
  }, [isPractitioner])

  if (loading) {
    return <PageLoadingIndicator />
  }

  if (!isPractitioner) {
    return null
  }

  // Handle lazy loading when sections are expanded
  const handleSectionExpand = async (section: string) => {
    switch (section) {
      case 'completedTreatments':
        await loadCompletedTreatmentsData()
        break
      case 'coPractitioner':
        await loadCoPractitionerData()
        break
      case 'completedCoPractitioner':
        await loadCompletedCoPractitionerData()
        break
    }
  }

  const PersonWithEditIcon = () => (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      <PersonIcon />
      <EditIcon style={{ 
        position: 'absolute', 
        bottom: 5, 
        right: 1, 
        fontSize: '12px',
        background: 'white',
        borderRadius: '50%'
      }} />
    </div>
  )

  return (
    <div>
      <div id="caseload-section">
        <CollapsiblePatientSection
          title={t('caseloadSectionTitle')}
          icon={PeopleAltOutlinedIcon}
          patients={caseloadData.patients}
          totalCount={caseloadData.count.numPatients}
          isExpanded={true}
          showPagination={false}
          searchEnabled={true}
          showIntakeWarnings={true}
          loading={caseloadData.loading}
          dateColumnTitle="Startdatum"
          showSessionsColumn={true}
          showSessionDot={true}
        />
      </div>

      <div id="completed-treatments-section">
        <CollapsiblePatientSection
          title={t('completedTreatmentsSectionTitle')}
          icon={AssignmentTurnedInIcon}
          patients={completedTreatmentsData.patients}
          totalCount={completedTreatmentsData.count.numPatients}
          isExpanded={false}
          showPagination={true}
          pageSize={20}
          searchEnabled={true}
          serverSideSearch={true}
          showIntakeWarnings={false}
          loading={completedTreatmentsData.loading}
          dateColumnTitle="Startdatum"
          showSessionsColumn={true}
          showSessionDot={false}
          onExpand={() => handleSectionExpand('completedTreatments')}
          onLoadMore={async (query) => {
            const patients = await client.getCompletedTreatments(query.pageSize, query.page, query.search)
            const processedPatients = processPatientData(patients)
            
            // For search results, calculate proper totalCount
            let totalCount = completedTreatmentsData.count.numPatients
            if (query.search) {
              // If searching and we got fewer results than pageSize, we know the total
              if (processedPatients.length < query.pageSize) {
                totalCount = (query.page * query.pageSize) + processedPatients.length
              } else {
                // More pages might exist, estimate conservatively
                totalCount = (query.page + 2) * query.pageSize // Allow at least one more page
              }
            }
            
            return {
              data: processedPatients,
              page: query.page,
              totalCount: totalCount
            }
          }}
        />
      </div>

      <div id="co-practitioner-section">
        <CollapsiblePatientSection
          title={t('coPractitionerSectionTitle')}
          icon={PersonWithEditIcon}
          patients={coPractitionerData.patients}
          totalCount={coPractitionerData.count.numPatients}
          isExpanded={false}
          showPagination={true}
          pageSize={20}
          searchEnabled={true}
          serverSideSearch={true}
          showIntakeWarnings={false}
          loading={coPractitionerData.loading}
          dateColumnTitle="Startdatum"
          showSessionsColumn={true}
          showSessionDot={true}
          onExpand={() => handleSectionExpand('coPractitioner')}
          onLoadMore={async (query) => {
            const patients = await client.getCoPractitionerPatients(query.pageSize, query.page, query.search)
            const processedPatients = processPatientData(patients)
            
            // For search results, calculate proper totalCount
            let totalCount = coPractitionerData.count.numPatients
            if (query.search) {
              // If searching and we got fewer results than pageSize, we know the total
              if (processedPatients.length < query.pageSize) {
                totalCount = (query.page * query.pageSize) + processedPatients.length
              } else {
                // More pages might exist, estimate conservatively
                totalCount = (query.page + 2) * query.pageSize // Allow at least one more page
              }
            }
            
            return {
              data: processedPatients,
              page: query.page,
              totalCount: totalCount
            }
          }}
        />
      </div>

      <div id="completed-co-practitioner-section">
        <CollapsiblePatientSection
          title={t('completedCoPractitionerSectionTitle')}
          icon={AssignmentTurnedInTwoToneIcon}
          patients={completedCoPractitionerData.patients}
          totalCount={completedCoPractitionerData.count.numPatients}
          isExpanded={false}
          showPagination={true}
          pageSize={20}
          searchEnabled={true}
          serverSideSearch={true}
          showIntakeWarnings={false}
          loading={completedCoPractitionerData.loading}
          dateColumnTitle="Startdatum"
          showSessionsColumn={true}
          showSessionDot={false}
          onExpand={() => handleSectionExpand('completedCoPractitioner')}
          onLoadMore={async (query) => {
            const patients = await client.getCompletedCoPractitionerTreatments(query.pageSize, query.page, query.search)
            const processedPatients = processPatientData(patients)
            
            // For search results, calculate proper totalCount
            let totalCount = completedCoPractitionerData.count.numPatients
            if (query.search) {
              // If searching and we got fewer results than pageSize, we know the total
              if (processedPatients.length < query.pageSize) {
                totalCount = (query.page * query.pageSize) + processedPatients.length
              } else {
                // More pages might exist, estimate conservatively
                totalCount = (query.page + 2) * query.pageSize // Allow at least one more page
              }
            }
            
            return {
              data: processedPatients,
              page: query.page,
              totalCount: totalCount
            }
          }}
        />
      </div>
    </div>
  )
}

export default PatientSelectPageContentNew
